import { logger } from 'features/logger/logger';
import { getAssetUrlWithTempLocationSupport } from 'features/questionnaires/store/questionnaireAssetApiSlice';
import { isNavigatingAway } from 'shared/utils/pageLeaveSignal';
import { readBodyOrThrow } from 'shared/utils/readBodyOrThrow';
import { createUrlWarmer } from 'shared/utils/urlWarmer';
import { urlWithoutQuery } from 'shared/utils/urlWithoutQuery';
import type { DbProtocolManifest } from '@hub/common';

const preloads = createUrlWarmer();

const logPreloadFailure = (url: string, error: unknown): void => {
  logger.error(
    `Unable to preload participation asset: ${urlWithoutQuery(url)}`,
    undefined,
    error as Error
  );
};

const preloadImage = async (url: string): Promise<void> => {
  const response = await fetch(url, { priority: 'low' });
  if (response.status >= 400 && response.status < 500) {
    if (isNavigatingAway()) return;
    logPreloadFailure(
      url,
      new Error(`Image request failed with ${response.status.toString()}`)
    );
    return;
  }
  await readBodyOrThrow(response, 'Image');
  logger.debug(`Preloaded participation asset: ${urlWithoutQuery(url)}`);
};

/** Loads a protocol manifest's images into the browser cache at low fetch
 *  priority, each URL once per page load; a URL whose load fails with a
 *  network error or a 5xx loads again on the next call. Never throws; a
 *  failure is logged, except while the page leaves. */
export const preloadParticipationAssets = (
  manifest: DbProtocolManifest
): void => {
  for (const imageUrl of Object.values(manifest.images ?? {})) {
    const url = getAssetUrlWithTempLocationSupport(imageUrl);
    preloads.warm(
      url,
      () => preloadImage(url),
      error => {
        logPreloadFailure(url, error);
      }
    );
  }
};
